import SurveyBaseController from './survey_base_controller';
import db from '../../field_db';
import { compressImageWithRetry, MAX_INPUT_BYTES, COMPRESSION_FAILURE, setHeicDecoderUrl } from '../../field_image_compressor';
import { queueImage, bankShadowImage, purgeForResponse, purgeSubmitted, purgeStale, discardByUrls, newCaptureId, placeIsPoorSignal, uploadPhotoFromMemory, startBackgroundUpload, backgroundUploadState, backgroundBusyCount, backgroundBusyIds, retryBackgroundNow, heldPhoto, isLost, photoPreviewBlob } from '../../field_image_queue';
import { logEvent, logError, logInfo, logWarn, setLogContext } from '../../field_logger';
import { renderPhotoPreview, previewUrls } from '../../lib/photo_preview';
import { removePhotoFromQuestion, isServerPhotoUrl } from '../../lib/photo_removal';
import { retargetTombstone, deleteServerPhoto } from '../../field_photo_delete_sync';
import { currentLevel, LEVEL_DEGRADED, LEVEL_OFFLINE } from '../../field_network_health';
import { timeoutFor } from '../../field_timeout_policy';
import { buildSubmissionMeta } from '../../field_submission_meta';
import { formatBytes } from '../../lib/format_bytes';
import { withImageFilename } from '../../lib/image_filename';
import { mergeLocalDraftOverServer, liveQueuedBlobUrls, setCachedResponseStatus, LOCAL_STATUS_COMPLETED_OFFLINE } from '../../field_sync_shared';
import { substituteUploadedUrls } from '../../field_upload_sync';
import { downloadMissionWithRetry, drainMissionDownloadQueue, queueMissionDownload, isMissionDownloadQueued, connectionLabel, MESSAGES as MISSION_MESSAGES } from '../../field_mission_download';
import { findBorrowableSurvey } from '../../field_borrowed_survey';
import { recordLedgerEntry, ACTIONS, checkRenderedStatusForReversion } from '../../field_response_ledger';
import { logSurveyRendered, logAnswerCountMismatch, answerCount } from '../../field_response_render_log';
import { shouldLogOnce } from '../../field_log_throttle';
import { takeCameraOpened } from '../../lib/camera_capture_session';

export default class extends SurveyBaseController {
  static targets = [
    "loader", "submitting", "loadError",
    // Card #4117 -- "This mission isn't downloaded yet" state and the notice
    // shown while a mission is open from a borrowed survey
    "notDownloaded", "downloadStatus", "downloadButton", "downloadButtonLabel", "borrowedNotice",
    // Card #4097 -- background photo upload (rendered only with the flag on)
    "photoTileTemplate", "gate", "gateTitle", "gateBody", "gateProgress", "gateLost",
    "gateWaitButton", "gateWaitSpinner", "gateWaitLabel",
    // Card #4594 -- "This mission is already finished, waiting to sync" state,
    // shown instead of the survey when a completion is already queued for
    // this response (see _checkCompletionWaiting).
    "completionWaiting", "completionWaitingIcon", "completionWaitingTitle", "completionWaitingBody"
  ]
  static values = {
    surveyableType: String,
    surveyableId: String,
    userId: String,
    completeUrl: String,
    missionName: String,
    placeName: String,
    placeId: String,
    heicDecoderUrl: String,
    // Card #4097 -- the place's Poor Signal flag as Rails knows it. Absent on
    // pages that don't render it; the device's place cache is the fallback.
    poorSignal: Boolean,
    // Card #4097 -- field:background_photo_upload. Off means today's awaited
    // capture upload, unchanged.
    backgroundUpload: { type: Boolean, default: false },
    // Card #4117 -- the server's own facts about THIS response, rendered into
    // the page (surveys/results/_edit.html.erb). Used to prove a sibling
    // response's survey is the identical definition before borrowing it, and
    // to give place variables when the response's own data cannot be fetched.
    missionId: String,
    surveyDigest: String,
    placeContext: Object,
    serverResultBlank: { type: Boolean, default: false },
    responseStatus: String,
    rangingConfirm: { type: Boolean, default: false },
    rangingHints: Object,
    rangingBarcodes: Object,
    // Card #4692 -- open allocation/TIO incoming-stock badge.
    rangingIncoming: Object
  }

  connect() {
    console.log("Connecting to survey result editor...");
    super.connect();

    // Ambient context for app_logger -- mission id isn't available client-side
    // here, but the server derives it from response_id when logs are synced.
    setLogContext({ responseId: this.surveyableIdValue, placeId: this.placeIdValue });

    // Card #3363 -- where the lazily-loaded HEIC decoder lives. Rendered by
    // _edit.html.erb so it's present even when this editor arrives inside a
    // Turbo Stream (desktop "Unlock to edit") rather than a full layout.
    if (this.hasHeicDecoderUrlValue) setHeicDecoderUrl(this.heicDecoderUrlValue);

    this.survey = this.initializeSurvey('edit');
    this._surveyLoaded = false;
    // True whenever viewSurvey() had to fall back to _showLoadError() (no
    // live fetch AND no cache to recover from) -- card #3804. Completing a
    // survey in that state means answers were never actually loaded, so
    // "Complete" would silently submit whatever empty/partial data survived.
    this._loadFailed = false;

    this.setupUploadHandlers();
    this.setupInAppCamera();
    this._submitting = false;
    this.survey.onComplete.add((survey, options) => {
      if (this._submitting) return;
      if (!this._confirmCompletionAllowed(survey.data)) return;
      this._submitting = true;
      // Card #4097 -- with background upload on, photos still sending get the
      // soft gate first (Wait or Finish now). Off: straight to submission.
      if (this.backgroundUploadValue) {
        this._submitAfterPhotoGate(options);
      } else {
        this._startSubmission(options);
      }
    });

    if (this.backgroundUploadValue) this._connectBackgroundUpload();

    // Autosave on every interaction so progress survives navigation
    this.survey.onValueChanged.add((survey) => {
      this._scheduleAutosave(survey.data)
    })

    // hide the lock div
    const lockDiv = document.getElementById(`response_${this.surveyableIdValue}_unlock_icon`);
    if (lockDiv) lockDiv.classList.add('hidden');

    // Listen for save-and-back events dispatched by the field back button
    this._saveBackHandler = (e) => this._saveAndBack(e.detail?.path)
    window.addEventListener("field:save-and-back", this._saveBackHandler)

    // Vault sweeps on open: stale orphans (safety net) + submitted images past grace
    purgeStale().catch(() => {})
    purgeSubmitted().catch(() => {})

    // Card #4117 -- a mission that finishes downloading elsewhere (the queue
    // draining on another page) opens here by itself; and while this page is
    // waiting on a queued download, nudge the queue every 30s so it does not
    // depend on the Tasker leaving and coming back.
    this._missionDownloadedHandler = (e) => this._onMissionDownloaded(e);
    window.addEventListener("field:mission-downloaded", this._missionDownloadedHandler);
    this._queueWatch = setInterval(() => this._nudgeQueue(), 30000);

    // Card #4594 -- a response with a completion already queued (tap Complete
    // offline, or a completion that failed to sync) must not be reopened and
    // redone: the tasker already finished it, and a second completion would
    // either duplicate the submission once both replay, or clobber the first
    // one's answers. Checked before the normal fetch/render path below, not
    // after -- this never touches the network.
    this._checkCompletionWaiting().then((waiting) => {
      // Fetch survey data first, then render — avoids the "no visible elements" flash
      if (!waiting) this.viewSurvey();
    });
  }

  // Returns true (and shows the completionWaiting state) when a pending_responses
  // row for this response already carries a completeUrl -- i.e. a completion is
  // queued, whether still waiting to send or rejected/stuck. Field app only
  // (completeUrlValue is blank for the admin/rt-win survey editor, which never
  // queues completions at all).
  async _checkCompletionWaiting() {
    if (!this.completeUrlValue) return false

    const rows = await db.pending_responses
      .where("responseId").equals(this.surveyableIdValue)
      .filter(r => !!r.completeUrl)
      .toArray()
    if (rows.length === 0) return false

    this._showCompletionWaiting(rows.some(r => r.status === "failed"))
    return true
  }

  _showCompletionWaiting(needsAttention) {
    if (this.hasLoaderTarget) this.loaderTarget.classList.add('hidden')
    if (!this.hasCompletionWaitingTarget) return

    if (this.hasCompletionWaitingTitleTarget) {
      this.completionWaitingTitleTarget.textContent = needsAttention
        ? "This mission needs attention"
        : "This mission is done"
    }
    if (this.hasCompletionWaitingBodyTarget) {
      this.completionWaitingBodyTarget.textContent = needsAttention
        ? "It finished, but the last sync attempt failed. It will keep retrying when you have signal."
        : "Saved and waiting to sync — it'll update automatically once you're back online."
    }
    if (this.hasCompletionWaitingIconTarget) {
      this.completionWaitingIconTarget.classList.toggle("bg-red-100", needsAttention)
      this.completionWaitingIconTarget.classList.toggle("text-red-500", needsAttention)
      this.completionWaitingIconTarget.classList.toggle("bg-emerald-100", !needsAttention)
      this.completionWaitingIconTarget.classList.toggle("text-emerald-500", !needsAttention)
    }
    this.completionWaitingTarget.classList.remove('hidden')

    logEvent("response", "Opened a response with a completion already queued -- blocked re-entry", {
      responseId:    this.surveyableIdValue,
      missionName:   this.missionNameValue || null,
      placeName:     this.placeNameValue   || null,
      needsAttention: needsAttention
    }).catch(() => {})
  }

  disconnect() {
    window.removeEventListener("field:save-and-back", this._saveBackHandler)
    window.removeEventListener("field:mission-downloaded", this._missionDownloadedHandler)
    clearInterval(this._queueWatch)
    clearTimeout(this._autosaveTimer)
    if (this.backgroundUploadValue) this._disconnectBackgroundUpload()
  }

  _startSubmission(surveyOptions = null) {
    surveyOptions?.showSaveInProgress?.();
    this._showSubmittingOverlay();
    this.processSurveyData().catch(error => {
      console.error('Survey submission failed:', error);
      this._submitting = false;
      this._hideSubmittingOverlay();
    });
  }

  // Leaving the editor after a save or completion. With background upload on
  // this is a Turbo visit, so the photos still sending (and the bytes held in
  // memory for them) survive the move; a full page load would throw both away
  // and leave an iPhone to retry from a stored copy it may not read back.
  _leaveEditor(url) {
    if (this.backgroundUploadValue && window.Turbo?.visit) {
      window.Turbo.visit(url)
    } else {
      window.location.href = url
    }
  }

  // Upserts (card #3804) -- previously UPDATE-only, so a response that was
  // never prefetched (or whose prefetch failed -- confirmed "Page warm-cache
  // failed" floods on the device that lost Richard Gourlay's answers) had no
  // cached_responses row for this to update, and autosave silently no-opped:
  // the tasker's answers only ever lived in the in-memory survey object,
  // gone the moment the tab was killed. Falls back to viewSurvey's own
  // surveyJson (`this.survey.toJSON()`) when creating a row here rather than
  // via _updateCache -- functionally equivalent to the schema last fetched,
  // since nothing in this controller mutates survey structure after load.
  _scheduleAutosave(data) {
    clearTimeout(this._autosaveTimer)
    this._autosaveTimer = setTimeout(async () => {
      let cacheWritten = false
      try {
        const existing = await db.cached_responses
          .where("responseId").equals(Number(this.surveyableIdValue))
          .first()
        if (existing) {
          await db.cached_responses.update(existing.id, { resultJson: data })
          cacheWritten = true
        } else {
          await db.cached_responses.add(Object.assign(this._newCachedResponseFields(), {
            surveyJson:   this.survey.toJSON(),
            resultJson:   data,
            // Card #4117 -- the response's own place data, not an empty object,
            // so a mission opened from a borrowed survey reopens with the right
            // place variables.
            placeContext: this._placeContext || {}
          }))
          cacheWritten = true
          logEvent("response", "Autosave created missing cached_responses row", {
            responseId: this.surveyableIdValue
          }).catch(() => {})
        }

        // Card #4688 -- a diagnostic trail of autosave activity: answer count
        // and whether the cache write actually landed. Throttled to once an
        // hour per response (field_log_throttle, the same once-per-hour
        // per-record convention card #3995 built) -- without it, a Tasker
        // editing for an hour would flood app_log with one line every 500ms
        // of typing. COUNTS ONLY, never the answers themselves.
        if (await shouldLogOnce(`autosave:${this.surveyableIdValue}`)) {
          logEvent("response", "Autosave", {
            responseId:  this.surveyableIdValue,
            answerCount: answerCount(data),
            cacheWritten
          }).catch(() => {})
        }
      } catch (error) {
        logError("response", "Autosave failed", {
          responseId: this.surveyableIdValue,
          message:    error?.message || String(error)
        }).catch(() => {})
      }
    }, 500)
  }

  _saveAndBack(path) {
    const dest = path || "/field"
    if (!this._surveyLoaded) {
      this._leaveEditor(dest)
      return
    }
    // _submitWithOfflineFallback now queues on any failure (card #2730) --
    // this .catch is a safety net for the online_only path (which still
    // throws by design, since online_only opts out of all local queuing) and
    // any other unexpected exception, not the mainline recovery path anymore.
    this._submitWithOfflineFallback(this.survey.data).then(({ queued }) => {
      if (!queued) this._leaveEditor(dest)
    }).catch(() => {
      this._leaveEditor(dest)
    })
  }

  viewSurvey() {
    // hit the API to get the survey data. Explicit timeout (ADR-0110 §4) --
    // this GET previously had none at all, so a hung connection left the
    // loader spinning indefinitely. json_read shortens to 5s in Degraded.
    // Card #4161 -- start time and stage feed the failure-cause log in the
    // catch below.
    const fetchStartedAt = Date.now()
    const timeoutMs = timeoutFor("json_read", currentLevel())
    let stage = "fetch"
    fetch(`/api/v1/surveys/results/results.json?surveyable_id=${this.surveyableIdValue}&surveyable_type=${this.surveyableTypeValue}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json;charset=UTF-8'
        },
        signal: AbortSignal.timeout(timeoutMs)
      })
      .then(response => {
        if (!response.ok) {
          const err = new Error("Failed to fetch survey")
          err.status = response.status
          throw err
        }
        stage = "body"
        return response.json();
      })
      .then(async data => {
        stage = "render"
        if (data.survey !== null) {
          this.convertPhotoTypeQuestionsToFileUpload(data.survey, this.responseStatusValue === 'ended');
          this.survey.fromJSON(data.survey);
          this._setPlaceVariables(data.place_context);
          // Cards #4031/#3073 -- last-visit ranging hints (null = field:ranging_confirm off).
          // Set before render so the ranging-status renderer sees it. Never part of survey.data.
          this._rangingHints = data.ranging_hints ?? null;
          this._rangingBarcodes = data.ranging_barcodes ?? null;
          this._rangingIncoming = data.ranging_incoming ?? null;
          // ADR-0113 -- the server's result is a baseline only, never the
          // final answer. A local cached_responses draft (autosaved every
          // 500ms via _scheduleAutosave, but never pushed to the server
          // until submit/back) can hold in-progress work this fetch knows
          // nothing about, e.g. after iOS backgrounds a PWA tab under memory
          // pressure and reloads it. Merge local over server before either
          // the on-screen survey or the cache is touched.
          // Card #4688 -- counts only, read BEFORE _mergeWithLocalDraft's own
          // query is overwritten by _updateCache below. Compares the server's
          // answer count against what this phone held going into this fetch
          // -- not the merged result, which is always the phone's draft
          // layered over the server's by design (ADR-0113) and would almost
          // always match the phone's own count.
          const localBeforeMerge = await db.cached_responses
            .where("responseId").equals(Number(this.surveyableIdValue))
            .first()
            .catch(() => null)
          if (localBeforeMerge?.resultJson) {
            logAnswerCountMismatch(this.surveyableIdValue, data.result, localBeforeMerge.resultJson).catch(() => {})
          }

          const mergedResult = await this._mergeWithLocalDraft(this.surveyableIdValue, data.result);
          this.survey.data = mergedResult;
          this._loadFailed = false;
          // Keep cached copy in sync with the merged result -- never regress
          // the local cache to a staler state than it already held. Upserts
          // (card #3804) -- the first successful viewSurvey() for a response
          // that was never prefetched (or whose prefetch failed) must create
          // this row, not silently no-op, or autosave has nothing to update.
          await this._updateCache(data.survey, mergedResult, data.place_context, data.ranging_hints, data.ranging_barcodes, data.ranging_incoming, data.survey_digest);

          // Render now that we have data — loader is still visible until this point
          const survey_div_id = `${this.surveyableTypeValue.toLowerCase()}_${this.surveyableIdValue}_survey_result_edit`;
          // Card #4222 -- everything above this point ran after an await, so the
          // page can have been torn down underneath us (Turbo navigating away
          // right after a tasker completes a survey is the common one). Look the
          // container up once, before rendering: gone means the tasker has
          // already left, which is normal, not a failure. Plain return so this
          // never falls through to the .catch and the cache fallback below.
          const surveyContainer = document.getElementById(survey_div_id);
          if (!this._renderTargetPresent(survey_div_id)) {
            this._logLoadAbandoned("fetch_render", survey_div_id);
            return;
          }
          this.survey.render(surveyContainer);
          this._surveyLoaded = true;

          // Hide loading spinner
          if (this.hasLoaderTarget) this.loaderTarget.remove();

          const customHeader = surveyContainer?.querySelector('#sv_custom_header');
          if (customHeader) customHeader.classList.add('hidden');
          this._collapseReferenceImages(surveyContainer);

          this.addCancelButton();

          // Card #4688 -- which copy rendered (always "server" on this live
          // path) plus the answer/photo-slot counts shown. Also checks
          // whether this screen load just saw a non-ended status for a
          // response the write ledger last recorded as ended.
          logSurveyRendered({
            responseId: this.surveyableIdValue,
            missionId:  this.missionIdValue,
            source:     "server",
            surveyJson: data.survey,
            resultJson: mergedResult
          }).catch(() => {})
          checkRenderedStatusForReversion(this.surveyableIdValue, this.responseStatusValue, { source: "server" }).catch(() => {})
        } else {
          // The server answered but holds no survey for this response. Never
          // leave the spinner up with nothing behind it.
          this._showLoadError();
        }
      })
      .catch(error => {
        console.error("Error loading survey:", error);
        this._logSurveyFetchFailure(error, stage, Date.now() - fetchStartedAt, timeoutMs)
        // Fall back to the IndexedDB cache on ANY failure -- confirmed
        // offline, a Degraded-mode timeout, or a genuine server error --
        // not just confirmed-offline (ADR-0110 §4: reads stay live with a
        // shortened timeout, falling back to the existing cache-read path
        // on failure). Reuses _loadFromCache() as-is; nothing new to build.
        this._loadFromCache().catch(cacheError => {
          // Never let the fallback itself escape as an unhandled rejection
          // (card #4007) -- show the retry state instead.
          logError("response", "Survey cache fallback failed", {
            responseId: this.surveyableIdValue,
            message:    cacheError?.message || String(cacheError)
          }).catch(() => {})
          this._showLoadError()
        })
      });
  }

  // Card #4161 -- the viewSurvey catch used to console.error the cause and
  // nothing else, so "Survey data failed to load and no cache was available"
  // could not say whether the server answered an error, the request timed out,
  // the browser aborted it (page left) or the network dropped. One small entry
  // per failure, before the cache fallback runs. No personal data: ids, a
  // cause label, timings and page state only.
  async _logSurveyFetchFailure(error, stage, elapsedMs, timeoutMs) {
    try {
      let cause = "network"
      if (stage === "render") cause = "client_error"
      else if (error?.status) cause = "http"
      else if (error?.name === "TimeoutError") cause = "timeout"
      else if (error?.name === "AbortError") cause = "abort"
      else if (stage === "body") cause = "body_unreadable"

      let hadCache = null
      try {
        const cached = await db.cached_responses.where("responseId").equals(Number(this.surveyableIdValue)).first()
        hadCache = !!(cached && cached.surveyJson)
      } catch { /* leave null: the cache read itself failed */ }

      logWarn("response", "Survey fetch failed", {
        responseId:  this.surveyableIdValue,
        missionId:   this.missionIdValue || null,
        cause:       cause,
        status:      error?.status ?? null,
        errorName:   error?.name || null,
        stage:       stage,
        elapsedMs:   elapsedMs,
        timeoutMs:   timeoutMs,
        hadCache:    hadCache,
        level:       connectionLabel(),
        connected:   this.element.isConnected,
        visibility:  document.visibilityState,
        submitting:  !!this._submitting
      }).catch(() => {})
    } catch { /* logging must never break the fallback */ }
  }

  // Reads survey data from cached_responses when the server is unreachable.
  async _loadFromCache() {
    // Card #4206 -- a tasker who already completed (or is mid-completing) the
    // survey, or has already left the page, has nobody left for a cache
    // fallback to serve. onComplete sets _submitting synchronously, well
    // before the completion POST and any navigation, so by the time this slow
    // fetch's catch handler gets here the race is already decided. Skip the
    // IndexedDB read and the render attempt entirely rather than letting them
    // run for nothing and risk re-surfacing stale data over what's on screen.
    if (this._loadRaceIsMoot()) {
      this._logLoadAbandoned("load_from_cache", null)
      return
    }

    const cached = await db.cached_responses
      .where("responseId").equals(Number(this.surveyableIdValue))
      .first()

    // No saved copy on this phone. Previously the loader was removed and a
    // blank white area was left (card #2841), then a retry state was added.
    // Now (card #4117) the field app first tries to let the Tasker keep
    // working from a sibling's identical survey, and otherwise says plainly
    // that the mission is not downloaded and offers to download it.
    if (!cached || !cached.surveyJson) {
      await this._handleNoLocalCopy(cached)
      return
    }

    await this._renderSnapshot(cached)
  }

  // Renders { surveyJson, placeContext, resultJson, rangingHints, rangingBarcodes, rangingIncoming }
  // into the survey container. Returns true when the survey is on screen.
  async _renderSnapshot(snapshot, { borrowed = false } = {}) {
    // Card #4007 -- this runs after an await (and usually after a failed or
    // timed-out fetch), so the page can have changed underneath it. Rendering
    // into a missing element made survey-js throw "null is not an object
    // (evaluating 't.__k')", leaving the loader up and the response empty.
    // That is why the container is looked up first, and it stays.
    //
    // Card #4222 -- what changed is what a missing container MEANS. It is the
    // page being torn down (Turbo navigating away right after a tasker
    // completes), not a load failure: there is nobody left on the page to show
    // a retry screen to. It used to log at error level and flip to the retry
    // state, which buried real failures under ~1000 harmless entries in two
    // days. Exit quietly instead -- one info line so the race stays countable.
    const surveyDivId = `${this.surveyableTypeValue.toLowerCase()}_${this.surveyableIdValue}_survey_result_edit`
    const surveyContainer = document.getElementById(surveyDivId)
    if (!this._renderTargetPresent(surveyDivId)) {
      this._showLoadError({ containerMissing: true })
      return false
    }

    try {
      this.convertPhotoTypeQuestionsToFileUpload(snapshot.surveyJson, this.responseStatusValue === 'ended')
      this.survey.fromJSON(snapshot.surveyJson)
      this._setPlaceVariables(snapshot.placeContext)
      this._rangingHints = snapshot.rangingHints ?? null
      this._rangingBarcodes = snapshot.rangingBarcodes ?? null
      this._rangingIncoming = snapshot.rangingIncoming ?? null
      this.survey.data = snapshot.resultJson || {}
      this.survey.render(surveyContainer)
    } catch (error) {
      logError("response", "Survey cache fallback render failed", {
        responseId: this.surveyableIdValue,
        message:    error?.message || String(error)
      }).catch(() => {})
      this._showLoadError()
      return false
    }
    this._surveyLoaded = true
    this._loadFailed = false

    if (this.hasLoaderTarget) this.loaderTarget.remove()
    if (this.hasNotDownloadedTarget) this.notDownloadedTarget.classList.add('hidden')
    const customHeader = surveyContainer?.querySelector('#sv_custom_header')
    if (customHeader) customHeader.classList.add('hidden')
    this._collapseReferenceImages(surveyContainer)
    this.addCancelButton()

    if (borrowed && this.hasBorrowedNoticeTarget) {
      this.borrowedNoticeTarget.classList.remove('hidden')
      // Keep the first question clear of the notice.
      surveyContainer.classList.add('pt-10')
    }

    // Card #4688 -- which copy rendered ("phone_cache" or "borrowed_sibling")
    // plus the answer/photo-slot counts shown, and (when this snapshot
    // carries its own status -- a direct cache read, never the synthetic
    // borrowed-sibling object) whether this offline screen load just saw a
    // non-ended status for a response the write ledger last recorded as ended.
    const source = borrowed ? "borrowed_sibling" : "phone_cache"
    logSurveyRendered({
      responseId: this.surveyableIdValue,
      missionId:  this.missionIdValue,
      source,
      surveyJson: snapshot.surveyJson,
      resultJson: snapshot.resultJson
    }).catch(() => {})
    if (snapshot.status) {
      checkRenderedStatusForReversion(this.surveyableIdValue, snapshot.status, { source }).catch(() => {})
    }

    return true
  }

  // Card #4117 -- this phone has no saved copy of the mission's survey and
  // the server could not be reached. Field app only; the admin editor keeps
  // its plain retry state.
  //
  // Order: (1) try to borrow an identical survey from a sibling response of
  // the same mission (field_borrowed_survey.js has every guard); (2) failing
  // that, show "This mission isn't downloaded yet" with a Download button.
  // Either way the miss is logged and a download is queued.
  async _handleNoLocalCopy(cached) {
    if (!this.completeUrlValue || !this.hasNotDownloadedTarget) {
      this._showLoadError()
      return
    }

    const page = {
      responseId:        this.surveyableIdValue,
      missionId:         this.missionIdValue,
      surveyDigest:      this.surveyDigestValue,
      serverResultBlank: this.serverResultBlankValue,
      status:            this.responseStatusValue
    }

    let borrow = { reason: "lookup_failed", mismatched: 0 }
    try {
      borrow = await findBorrowableSurvey(db, page)
    } catch (error) {
      logError("response", "Borrowed survey lookup failed", {
        responseId: this.surveyableIdValue, message: error?.message || String(error)
      }).catch(() => {})
    }

    if (borrow.surveyJson) {
      const shown = await this._renderSnapshot({
        surveyJson:   borrow.surveyJson,
        // This response's OWN place data, from the page the server rendered
        // for it. Never the sibling's.
        placeContext: this.placeContextValue,
        // Only this phone's own draft, if any. The server holds no answers
        // (serverResultBlank was a guard), and the sibling's are never used.
        resultJson:   cached?.resultJson || {},
        // Also this response's own (null when ranging confirm is off).
        rangingHints:    this.rangingConfirmValue ? this.rangingHintsValue : null,
        rangingBarcodes: this.rangingConfirmValue ? this.rangingBarcodesValue : null,
        rangingIncoming: this.rangingConfirmValue ? this.rangingIncomingValue : null
      }, { borrowed: true })

      if (shown) {
        this._borrowed = true
        logEvent("response", "Mission opened from borrowed survey", {
          responseId:       this.surveyableIdValue,
          missionId:        this.missionIdValue,
          source:           `cached response ${borrow.sibling.responseId}`,
          sourceResponseId: borrow.sibling.responseId,
          surveyDigest:     this.surveyDigestValue,
          reason:           "survey data failed to load and this mission was not downloaded",
          level:            connectionLabel(),
          otherVersionsSkipped: borrow.mismatched
        }).catch(() => {})
        // Still get the real thing down, in the background.
        queueMissionDownload(this.surveyableIdValue, { reason: "borrowed_survey" })
        this._nudgeQueue()
        return
      }
      // The render failed and already showed the retry state.
      return
    }

    logEvent("response", "Mission not borrowable", {
      responseId: this.surveyableIdValue, missionId: this.missionIdValue || null,
      reason: borrow.reason, otherVersionsSkipped: borrow.mismatched || 0, level: connectionLabel()
    }).catch(() => {})
    this._showNotDownloaded()
  }

  // Card #4222 -- the editor is still on the page and its render target still
  // exists. Everything that renders runs after an await (fetch, local-draft
  // merge, cache read), so the page can be torn down underneath it -- Turbo
  // navigating away right after a tasker completes a survey is the common one.
  _renderTargetPresent(surveyDivId) {
    return this.element.isConnected && !!document.getElementById(surveyDivId)
  }

  // Card #4206 -- true whenever a load-failure signal arriving now would be
  // noise rather than a genuine failure: the page is already torn down
  // (#4222's case), or the tasker has already completed the survey, or a
  // completion is in flight. _submitting is set synchronously by onComplete,
  // before the completion POST or any navigation, so this is reliable even
  // when the container itself is still present and connected.
  _loadRaceIsMoot() {
    return !this.element.isConnected || this._submitting
  }

  // Card #4534 -- SurveyJS's native "image" question type is how authors drop
  // a reference/instructional picture into a mission (e.g. a diagram for the
  // NEXT question), not something a Tasker fills in. Both render paths below
  // used to hide every .sd-image outright (a prior change meant to cut
  // clutter), which also hid genuinely useful reference images the moment a
  // Tasker opened the mission to start/continue it -- they only ever showed
  // up in the admin preview. This swaps the blind hide for an expanded-by-
  // default, click-to-hide toggle rendered under the question title: the
  // image shows straight away, and the Tasker can collapse it out of the way
  // (then bring it back) in place (no modal, no page jump, no scroll loss).
  // Uniform across every mission, old and new -- no author/editor setting,
  // by design.
  //
  // Card #4534 review (Vanessa, second pass) -- flips the toggle's initial
  // state from collapsed to expanded: the image should show by default, with
  // the toggle there to hide it if the Tasker wants, not the other way round.
  //
  // Shared tail for both render paths (live fetch success in viewSurvey,
  // and the cached-snapshot path in _renderSnapshot) -- one implementation,
  // not two, matching this file's existing convention for shared post-render
  // steps (see addCancelButton).
  _collapseReferenceImages(surveyContainer) {
    surveyContainer?.querySelectorAll('.sd-image').forEach(imageDiv => {
      // survey.render() rebuilds this node fresh on every call, so there is
      // no stale toggle to find on the usual path -- this guard only stops a
      // double-wrap if something ever re-runs this against the same live DOM.
      if (imageDiv.previousElementSibling?.classList?.contains('sd-image-toggle')) return

      // Card #4534 review (Vela) -- gives the image an id to hang aria-controls
      // off, without clobbering one SurveyJS may already have set.
      if (!imageDiv.id) imageDiv.id = `sd-image-${Math.random().toString(36).slice(2)}`

      const toggle = document.createElement('button')
      toggle.type = 'button'
      toggle.setAttribute('aria-expanded', 'true')
      toggle.setAttribute('aria-controls', imageDiv.id)
      // py-2 (Vela review) -- a bare text-sm line box is well under a usable
      // mobile tap target; this is a Tasker-facing field app toggle sitting
      // under a question title, same spirit as field-section-toggle's own
      // py-3 button (app/views/field/places/_sorted_list.html.erb).
      toggle.className = 'sd-image-toggle flex items-center gap-1 py-2 text-sm text-gray-500 hover:text-gray-700'
      toggle.innerHTML = `
        <svg class="sd-image-toggle__chevron rotate-180 w-4 h-4 transition-transform duration-150" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd" />
        </svg>
        <span class="sd-image-toggle__label">Hide reference image</span>
      `

      toggle.addEventListener('click', () => {
        const nowHidden = imageDiv.classList.toggle('hidden')
        toggle.querySelector('.sd-image-toggle__chevron').classList.toggle('rotate-180', !nowHidden)
        toggle.querySelector('.sd-image-toggle__label').textContent = nowHidden ? 'Show reference image' : 'Hide reference image'
        toggle.setAttribute('aria-expanded', String(!nowHidden))
      })

      imageDiv.insertAdjacentElement('beforebegin', toggle)
    })
  }

  // Teardown is expected, not a failure: one quiet line so the race is still
  // countable, without it looking like a tasker lost their mission.
  _logLoadAbandoned(where, surveyDivId) {
    logInfo("response", "Survey render abandoned -- page left before render", {
      responseId:  this.surveyableIdValue,
      missionId:   this.missionIdValue || null,
      where:       where,
      surveyDivId: surveyDivId || null,
      connected:   this.element.isConnected,
      // Card #4206 -- distinguishes a completed/submitting race from a plain
      // torn-down page in the same quiet log line.
      submitting:  !!this._submitting,
      visibility:  document.visibilityState
    }).catch(() => {})
  }

  // Shown when the live fetch failed/timed out AND there was no cached copy
  // to fall back on (card #2841). Logged via app_logger so a recurring
  // instance is visible without waiting for a Tasker to report it -- the
  // "fail loudly, don't silently drop" doctrine ADR-0110 established for
  // writes applies here too, just for a read.
  //
  // Card #4222 -- except when there is no page left to fail on. A missing
  // render target (or an editor already off the DOM) is page teardown, so take
  // the quiet exit: one info line, no error, no retry screen nobody can see.
  // Deliberately returns WITHOUT setting _loadFailed -- that flag exists to
  // gate a tasker completing a survey whose answers never loaded, and there is
  // no tasker left on this page for it to protect.
  //
  // Card #4206 -- same quiet exit now also covers a load path resolving after
  // the tasker already completed, or is mid-completing, the survey --
  // _loadRaceIsMoot() catches both that and the disconnected-page case.
  _showLoadError({ containerMissing = false } = {}) {
    if (containerMissing || this._loadRaceIsMoot()) {
      this._logLoadAbandoned("load_error", null)
      return
    }

    this._loadFailed = true
    if (this.hasLoaderTarget) this.loaderTarget.classList.add('hidden')
    if (this.hasLoadErrorTarget) this.loadErrorTarget.classList.remove('hidden')
    logError("response", "Survey data failed to load and no cache was available", {
      responseId:  this.surveyableIdValue,
      missionName: this.missionNameValue || null,
      placeName:   this.placeNameValue   || null,
      level:       connectionLabel()
    }).catch(() => {})
  }

  // Card #4117 -- "This mission isn't downloaded yet". Same terminal log line
  // as _showLoadError (Signal and the diagnostics page look for it), then the
  // Download button. If the phone is offline say so straight away and queue
  // the download, so it happens by itself when a connection returns.
  //
  // Card #4206 -- this path used to log unconditionally, unlike
  // _showLoadError, so a borrow lookup resolving after the tasker completed
  // (or after the page was torn down) still shouted. Same quiet exit now.
  _showNotDownloaded() {
    if (this._loadRaceIsMoot()) {
      this._logLoadAbandoned("not_downloaded", null)
      return
    }

    this._loadFailed = true
    if (this.hasLoaderTarget) this.loaderTarget.classList.add('hidden')
    this.notDownloadedTarget.classList.remove('hidden')

    logError("response", "Survey data failed to load and no cache was available", {
      responseId:  this.surveyableIdValue,
      missionName: this.missionNameValue || null,
      placeName:   this.placeNameValue   || null,
      level:       connectionLabel()
    }).catch(() => {})

    if (currentLevel() === LEVEL_OFFLINE) {
      queueMissionDownload(this.surveyableIdValue, { reason: "offline_on_open" })
      this._setDownloadStatus(MISSION_MESSAGES.offline)
    } else if (isMissionDownloadQueued(this.surveyableIdValue)) {
      this._setDownloadStatus(MISSION_MESSAGES.weak)
    }
  }

  _setDownloadStatus(message) {
    if (this.hasDownloadStatusTarget) this.downloadStatusTarget.textContent = message
  }

  // The Download this mission button.
  async downloadMission() {
    if (this._downloading) return
    this._downloading = true
    if (this.hasDownloadButtonTarget) this.downloadButtonTarget.disabled = true

    try {
      const result = await downloadMissionWithRetry(this.surveyableIdValue, {
        onState: ({ message }) => this._setDownloadStatus(message)
      })
      if (result.state === "downloaded") {
        await this._afterDownload()
      } else if (this.hasDownloadButtonLabelTarget) {
        this.downloadButtonLabelTarget.textContent = "Try again"
      }
    } finally {
      this._downloading = false
      if (this.hasDownloadButtonTarget) this.downloadButtonTarget.disabled = false
    }
  }

  // The mission is now in IndexedDB: open it from there.
  async _afterDownload() {
    if (this._surveyLoaded) return
    if (this.hasNotDownloadedTarget) this.notDownloadedTarget.classList.add('hidden')
    if (this.hasLoaderTarget) this.loaderTarget.classList.remove('hidden')
    await this._loadFromCache()
  }

  // Another part of the app finished downloading a mission.
  _onMissionDownloaded(event) {
    if (String(event.detail?.responseId) !== String(this.surveyableIdValue)) return
    if (this._borrowed && this.hasBorrowedNoticeTarget) {
      // Already working: the full copy is now on the phone for next time. Do not
      // swap the survey under the Tasker's fingers.
      this.borrowedNoticeTarget.classList.add('hidden')
      return
    }
    if (!this._surveyLoaded && !this._downloading) this._afterDownload()
  }

  _nudgeQueue() {
    if (!this._borrowed && !this._loadFailed) return
    drainMissionDownloadQueue({ trigger: "editor" }).catch(() => {})
  }

  // Retry affordance on the load-error state -- re-runs the fetch/cache path
  // from scratch, same as the original page load.
  retryLoad() {
    if (this.hasLoadErrorTarget) this.loadErrorTarget.classList.add('hidden')
    if (this.hasLoaderTarget) this.loaderTarget.classList.remove('hidden')
    this.viewSurvey()
  }

  // Card #3804 -- a tasker completing a survey whose data never actually
  // loaded (failed fetch AND no cache to fall back on) or that has zero
  // captured answers (e.g. a sync writer clobbered a local draft before this
  // tab re-read it, or the server itself never held anything but
  // submission_meta) would otherwise silently submit blank/partial data with
  // no chance to notice. Gate completion behind a confirm dialog in either
  // case; log so a recurring instance is visible without waiting for a
  // tasker to report it.
  _confirmCompletionAllowed(data) {
    const isEmpty = !data || Object.keys(data).length === 0
    if (!this._loadFailed && !isEmpty) return true

    const noun = this.completeUrlValue ? "mission" : "survey"
    const proceed = window.confirm(
      `This ${noun} doesn't appear to have any answers saved. Complete it anyway?`
    )
    if (proceed) {
      logEvent("response", "Survey completed with empty data", {
        responseId:  this.surveyableIdValue,
        missionName: this.missionNameValue || null,
        placeName:   this.placeNameValue   || null,
        loadFailed:  this._loadFailed
      }).catch(() => {})
    }
    return proceed
  }

  // Injects place attributes as SurveyJS variables so visibleIf expressions
  // can reference them (e.g. {place.size} = 'large').
  _setPlaceVariables(placeContext) {
    if (!placeContext || typeof placeContext !== 'object') return
    this._placeContext = placeContext
    for (const [key, value] of Object.entries(placeContext)) {
      this.survey.setVariable(key, value)
    }
  }

  // ADR-0113 -- merges a local cached_responses draft over a freshly-fetched
  // server result, local field wins. Same shape as rt-client's
  // restoreSurveyDataWithFallback: a flat, key-by-key precedence with no
  // timestamp comparison, since there is only ever one tasker on one device
  // writing to a given response. No local cache yet (first-ever load of
  // this response) -- nothing to protect, the server result is used as-is.
  async _mergeWithLocalDraft(surveyableId, serverResult) {
    const existing = await db.cached_responses
      .where("responseId").equals(Number(surveyableId))
      .first()

    // Live queue check (review fix, #3796/#3797) -- a blob: still queued in
    // pending_images is a photo in flight (retake / multi-photo add), not a
    // dead placeholder, so it must not be superseded by the server's value.
    const liveBlobUrls = existing?.resultJson ? await liveQueuedBlobUrls(db) : new Set()
    return mergeLocalDraftOverServer(existing?.resultJson, serverResult, liveBlobUrls)
  }

  // The shared defaults for a brand-new cached_responses row, used whenever
  // this controller has to create one rather than update an existing row
  // (card #3804) -- viewSurvey's first successful load for a response that
  // was never prefetched, or autosave discovering the row is missing.
  _newCachedResponseFields() {
    return {
      responseId:  Number(this.surveyableIdValue),
      placeId:     this.placeIdValue ? Number(this.placeIdValue) : null,
      cachedAt:    new Date().toISOString(),
      status:      "started",
      missionName: this.missionNameValue || null,
      completeUrl: this.completeUrlValue || null,
      // Card #4117 -- lets this row lend, or be matched to, a sibling's survey.
      missionId:    this.missionIdValue ? Number(this.missionIdValue) : null,
      surveyDigest: this.surveyDigestValue || null
    }
  }

  // Writes the latest server-side survey/result data back to the cache so that
  // loadFromCache always reflects the current state. Upserts (card #3804) --
  // creates the row (with surveyJson, placeContext, completeUrl and status)
  // when this response was never prefetched, or prefetch failed, so it's
  // never a silent no-op that leaves autosave with nothing to update.
  async _updateCache(surveyJson, resultJson, placeContext, rangingHints, rangingBarcodes, rangingIncoming, surveyDigest) {
    const existing = await db.cached_responses
      .where("responseId").equals(Number(this.surveyableIdValue))
      .first()

    const fields = {
      surveyJson,
      resultJson:   resultJson || {},
      placeContext: placeContext || {},
      // Last-visit hints cached beside the survey for offline use (cards #4031/#3073).
      rangingHints: rangingHints ?? null,
      rangingBarcodes: rangingBarcodes ?? null,
      // Open allocation/TIO incoming-stock badge (card #4692), same rules.
      rangingIncoming: rangingIncoming ?? null
    }
    // Card #4117 -- stamp the fingerprint so this row can lend its survey.
    if (surveyDigest) fields.surveyDigest = surveyDigest
    if (this.missionIdValue) fields.missionId = Number(this.missionIdValue)

    if (existing) {
      await db.cached_responses.update(existing.id, fields)
    } else {
      await db.cached_responses.add(Object.assign(this._newCachedResponseFields(), fields))
      logEvent("response", "Created cached_responses row on first load", {
        responseId: this.surveyableIdValue
      }).catch(() => {})
    }
  }

  async processSurveyData() {
    const data = this.survey.data;
    if (this.completeUrlValue) {
      // Field app: save data then POST to complete endpoint and navigate to place.
      // Network-first: try server, fall back to offline queue only if confirmed offline.
      const { queued } = await this._submitWithOfflineFallback(data, this.completeUrlValue);
      if (queued) return; // Already showed offline message and navigated
      const csrfToken = document.querySelector("[name='csrf-token']").content;
      // Card #3827 -- the completion intent is written BEFORE the live POST,
      // not only in the catch below: a navigation, reload or kill while the
      // POST is in flight used to leave nothing to replay (UAT #1351058
      // stayed "started"). Same pending_responses shape _queueCompletion
      // writes, so the existing replay finishes it; removed on success.
      const intentId = await this._writeCompletionIntent(data);
      const localStatusBefore = this.responseStatusValue || null
      let response = null
      try {
        response = await fetch(this.completeUrlValue, {
          method: 'POST',
          headers: { 'X-CSRF-Token': csrfToken },
          redirect: 'follow',
          signal: AbortSignal.timeout(timeoutFor("json_write_light", currentLevel()))
        });
        // Card #4688 -- one ledger entry for this write. #complete always
        // redirects, so fetch's default redirect: "follow" means `response`
        // here is the LANDED page's, not this POST's own -- requestId is
        // left null rather than attribute the landed page's request id to
        // this write (see field_response_ledger.js's header comment).
        recordLedgerEntry(this.surveyableIdValue, {
          action:            ACTIONS.COMPLETE,
          localStatusBefore,
          localStatusAfter:  response.ok ? "ended" : localStatusBefore,
          httpStatus:        response.status,
          serverStatus:      response.headers.get("X-WIN-Response-Status"),
          requestId:         null
        }).catch(() => {})

        if (!response.ok) {
          const err = new Error(`Server error: ${response.status}`)
          err.status = response.status
          throw err
        }
        await this._clearCompletionIntent(intentId);
        await setCachedResponseStatus(db, this.surveyableIdValue, "ended").catch(error => {
          logError("response", "Cached response status write failed", {
            responseId: this.surveyableIdValue, status: "ended", message: error?.message || null
          }).catch(() => {})
        });
        this._leaveEditor(response.url || '/field');
      } catch (error) {
        // Timeout (AbortError), network failure, or a non-2xx status (e.g. the
        // 504 Therese Simmonds hit, card #2652) -- the survey data itself is
        // already durably saved (the PUT inside _submitWithOfflineFallback above
        // succeeded), but the completion call didn't land. Without this, the
        // "submitting" overlay stays up forever with no recovery (Card #2435).
        // Queue completion for replay via the same completeUrl path the
        // service worker and foreground sync controller already understand
        // (field_upload_sync.js#replayResponse), rather than leaving the
        // tasker stuck.
        if (!response) {
          // Card #4688 -- fetch threw before any response landed (timeout or
          // network error) -- still one ledger entry for the attempt.
          recordLedgerEntry(this.surveyableIdValue, {
            action: ACTIONS.COMPLETE, localStatusBefore, localStatusAfter: localStatusBefore
          }).catch(() => {})
        }
        logError("response", "Survey completion request failed, queued for retry", {
          responseId:  this.surveyableIdValue,
          missionName: this.missionNameValue || null,
          placeName:   this.placeNameValue   || null,
          status:      error?.status ?? null,
          message:     error?.message || null
        }).catch(() => {})
        // The intent row written above IS the queued completion -- only fall
        // back to writing one if that write failed, so there is never a
        // second row for the same completion.
        if (intentId == null) {
          await this._queueCompletion(data)
        } else {
          navigator.serviceWorker?.ready?.then(reg => reg.sync?.register('field-upload-sync')).catch(() => {})
        }
        this._showOfflineToast()
        setTimeout(() => { this._leaveEditor('/field') }, 2000)
      }
    } else {
      // Standard edit mode: save and reload turbo frame
      await this.submitSurveyData(data);
      // Save confirmed on the server -- clean the vault for this response:
      // shadows purged, uploaded queued captures stamped submitted, pending
      // captures untouched (W2).
      await purgeForResponse(this.surveyableIdValue).catch(() => {});
      this._hideSubmittingOverlay();
      this._submitting = false;
      const displayFrameId = `${this.surveyableTypeValue.toLowerCase()}_${this.surveyableIdValue}_survey_result_edit`;
      const displayTurboFrame = document.getElementById(displayFrameId);
      if (displayTurboFrame) {
        displayTurboFrame.setAttribute(
          'src',
          `/surveys/results/1.turbo_stream?surveyable_id=${this.surveyableIdValue}&surveyable_type=${this.surveyableTypeValue}`
        );
      }
    }
  }

  // Network-first submit with offline fallback.
  // Tries the server. If the fetch fails and we're confirmed offline (via pixel polling),
  // queues to IndexedDB and shows an offline message. Returns { queued: true } when offline.
  async _submitWithOfflineFallback(data, completeUrl = null) {
    // Check offline mode preference -- online_only skips all queuing
    const offlineMode = localStorage.getItem("field:offline_mode") || "auto"
    if (offlineMode === "online_only") {
      await this.submitSurveyData(data)
      // Confirmed synced -- clean the vault for this response (shadows purged,
      // uploaded queued captures stamped submitted, pending untouched) (W2).
      await purgeForResponse(this.surveyableIdValue).catch(() => {})
      return { queued: false }
    }

    // Degraded (ADR-0110 §4): skip the live attempt entirely and go straight
    // to the offline queue -- we already have evidence (Card 1's hysteresis)
    // that this connection is likely to hang, so don't spend a timeout
    // window re-discovering that before queueing. This is a genuine
    // behavioural branch, distinct from the catch-path below (Online,
    // attempted-then-failed).
    if (currentLevel() === LEVEL_DEGRADED) {
      await this._queueSurveyData(data, completeUrl)
      return { queued: true }
    }

    try {
      await this.submitSurveyData(data)
      logEvent("response", "Survey completed", {
        responseId:  this.surveyableIdValue,
        missionName: this.missionNameValue || null,
        placeName:   this.placeNameValue   || null
      }).catch(() => {})
      // Response result is now durably on the server (PUT 200) with the server
      // image URLs already in the survey data. Clean the vault for this
      // response: shadow recovery copies are purged, already-uploaded queued
      // captures are stamped submitted (swept after the grace period), and
      // pending captures are left untouched (W2 point 3, ADR-0048 §3).
      await purgeForResponse(this.surveyableIdValue).catch(() => {})
      return { queued: false }
    } catch (error) {
      // Queue on ANY failure, not just confirmed-offline (card #2730) -- a
      // genuine server error/timeout while still "online" per the pixel-poll
      // flag (e.g. the 504 in card #2652) must not propagate past this point:
      // _saveAndBack's bare .catch(() => navigate away) discarded the write
      // silently, with no recovery. Mirrors uploadSingleFile's queue-on-any-
      // failure fallback (card #2632) rather than gating solely on the
      // confirmed-offline signal.
      const isOffline = localStorage.getItem("field:online") === "false"
      if (!isOffline) {
        // Still worth a dedicated error log distinct from the "queued for
        // sync" info log below -- a confirmed-offline queue is expected, a
        // genuinely-online failure is not, and without this a completed
        // round-trip that came back with an error status was invisible
        // (only thrown/network errors reached logError before this fix).
        logError("response", "Survey data save failed with server error", {
          responseId:  this.surveyableIdValue,
          missionName: this.missionNameValue || null,
          placeName:   this.placeNameValue   || null,
          status:      error?.status ?? null,
          message:     error?.message || null
        }).catch(() => {})
      }

      await this._queueSurveyData(data, completeUrl)
      return { queued: true }
    }
  }

  // Shared queuing tail for _submitWithOfflineFallback's two "goes to the
  // queue" paths (Degraded skip-live-attempt above, and Online
  // attempted-then-failed in the catch above) -- one implementation, not
  // two, matching this codebase's existing convention (see
  // field_network_health.js's header comment for the same principle applied
  // to leaf modules).
  async _queueSurveyData(data, completeUrl) {
    this._ensureSubmissionMeta(data)
    await db.pending_responses.add({
      responseId:     this.surveyableIdValue,
      surveyableType: this.surveyableTypeValue,
      // Card #3827 (B1) -- already-uploaded photos go in as their server URL.
      surveyData:     await this._withUploadedPhotoUrls(data),
      completeUrl:    completeUrl,
      missionName:    this.missionNameValue || null,
      placeName:      this.placeNameValue   || null,
      placeId:        this.placeIdValue     || null,
      status:         "pending",
      queuedAt:       new Date().toISOString()
    })

    // Card #4594 -- completeUrl present means this queued row IS a completion
    // (not a plain "save and back" draft, which queues with completeUrl null
    // via _saveAndBack). Flip the local cache straight away so every page
    // reading cached_responses (place, Today, the response card's own badge)
    // shows "done, sending" instead of whatever status was last synced.
    if (completeUrl) {
      await setCachedResponseStatus(db, this.surveyableIdValue, LOCAL_STATUS_COMPLETED_OFFLINE).catch(() => {})
    }

    // Register Background Sync so the SW can replay this when online
    navigator.serviceWorker?.ready?.then(reg => reg.sync?.register('field-upload-sync')).catch(() => {})

    logEvent("response", "Survey completed offline, queued for sync", {
      responseId:  this.surveyableIdValue,
      missionName: this.missionNameValue || null,
      placeName:   this.placeNameValue   || null
    }).catch(() => {})

    // Immediate flush attempt (ADR-0110 §4) -- field_sync_controller already
    // listens for this event and runs the shared sync() under
    // SYNC_LOCK_NAME, so a genuinely-fine request still completes in a few
    // seconds rather than waiting for the next scheduled sync pass. A no-op
    // when actually offline: field_sync_controller#sync bails out
    // immediately while field:online is false.
    window.dispatchEvent(new CustomEvent("field:sync-now"))

    this._showOfflineToast()
    // Navigate back to place after a brief delay so the toast is visible
    setTimeout(() => { this._leaveEditor('/field') }, 2000)
  }

  // Card #3827 -- the completion intent written before the live complete POST
  // (processSurveyData). Deliberately does NOT register Background Sync: the
  // live POST is already on its way, and the catch path registers it if that
  // POST fails. Returns the row id, or null if the write failed (the catch
  // path then falls back to _queueCompletion).
  async _writeCompletionIntent(data) {
    try {
      const id = await db.pending_responses.add(await this._completionRecord(data))
      // Card #4594 -- written optimistically before the live POST even starts,
      // so the local state says "done, sending" immediately. If the live POST
      // (below, in processSurveyData) succeeds first, it overwrites this with
      // the server's real "ended" a few lines later -- this is never the
      // final word, just the fastest-available one.
      await setCachedResponseStatus(db, this.surveyableIdValue, LOCAL_STATUS_COMPLETED_OFFLINE).catch(() => {})
      return id
    } catch (error) {
      logError("response", "Completion intent write failed", {
        responseId: this.surveyableIdValue,
        message:    error?.message || null
      }).catch(() => {})
      return null
    }
  }

  async _clearCompletionIntent(intentId) {
    if (intentId == null) return
    await db.pending_responses.delete(intentId).catch(error => {
      logError("response", "Completion intent cleanup failed", {
        responseId: this.surveyableIdValue,
        message:    error?.message || null
      }).catch(() => {})
    })
  }

  async _completionRecord(data) {
    return {
      responseId:     this.surveyableIdValue,
      surveyableType: this.surveyableTypeValue,
      // Card #3827 (B1) -- already-uploaded photos go in as their server URL.
      surveyData:     await this._withUploadedPhotoUrls(data),
      completeUrl:    this.completeUrlValue,
      missionName:    this.missionNameValue || null,
      placeName:      this.placeNameValue   || null,
      placeId:        this.placeIdValue     || null,
      status:         "pending",
      queuedAt:       new Date().toISOString()
    }
  }

  // Queues a completion-only replay when the direct completeUrl POST fails
  // after survey data has already been saved (Card #2435). Reuses the exact
  // pending_responses shape the offline-fallback path above and the shared
  // replay code (field_upload_sync.js#replayResponse, called from both the
  // foreground sync controller and the service worker's Background Sync
  // handler) already know how to replay -- PUT the data again (idempotent,
  // it already matches the server), then POST completeUrl.
  async _queueCompletion(data) {
    await db.pending_responses.add(await this._completionRecord(data))
    // Card #4594 -- same flip as _writeCompletionIntent; this is the fallback
    // path when that optimistic write itself failed, so the row (and the
    // local state) is only set here instead.
    await setCachedResponseStatus(db, this.surveyableIdValue, LOCAL_STATUS_COMPLETED_OFFLINE).catch(() => {})

    // Register Background Sync so the SW can replay this when online
    navigator.serviceWorker?.ready?.then(reg => reg.sync?.register('field-upload-sync')).catch(() => {})
  }

  _showOfflineToast() {
    const toast = document.createElement('div')
    toast.className = 'fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl shadow-lg text-sm font-medium text-amber-800'
    toast.innerHTML = `
      <span class="w-2 h-2 rounded-full bg-amber-400 shrink-0"></span>
      Saved offline &mdash; will sync when connected
    `
    document.body.appendChild(toast)
  }

  _showSubmittingOverlay() {
    if (this.hasSubmittingTarget) this.submittingTarget.classList.remove('hidden');
  }

  _hideSubmittingOverlay() {
    if (this.hasSubmittingTarget) this.submittingTarget.classList.add('hidden');
  }

  saveSurveyData() {
    const data = this.survey.data;
    this.submitSurveyData(data);
  }

  // Attaches _submission_meta (card #2831) the first time a given submission's
  // data object passes through either send path (submitSurveyData for the
  // direct/online PUT, _queueSurveyData for the Degraded-mode skip-straight-
  // to-queue path). Idempotent -- data and _queueSurveyData/_queueCompletion
  // share the SAME object reference throughout a submission (see
  // _submitWithOfflineFallback), so whichever runs first sets it and later
  // calls in the same chain leave it untouched. buildSubmissionMeta returns
  // null off the field app (no app-version meta tag -- the admin/rt-win
  // survey editor shares this controller), in which case nothing is attached.
  //
  // This is also the single snapshot point for _submission_meta.captured_at
  // (card #2872) -- both _saveAndBack and processSurveyData funnel through
  // here (via _submitWithOfflineFallback -> submitSurveyData/_queueSurveyData)
  // before the data object is ever queued to Dexie or sent live, so
  // captured_at is stamped once, at snapshot time, for either call site --
  // no need to attach it separately at each. See field_submission_meta.js
  // for why it must never be regenerated on retry/replay.
  _ensureSubmissionMeta(data) {
    if (!data || data._submission_meta) return
    const meta = buildSubmissionMeta(this.survey, data)
    if (meta) data._submission_meta = meta
  }

  // Card #3827 (B1) -- a copy of `data` for a request body or queued record,
  // with each photo already uploaded for this response swapped from its
  // device-only blob: link to its server URL. The PUT replaces result_json
  // wholesale, so sending the blob: link would overwrite the server's attach
  // (a photo queued in Degraded that uploaded via field:sync-now mid-edit).
  // The live survey model is never changed. Best effort: on any failure the
  // data goes as it was.
  async _withUploadedPhotoUrls(data) {
    try {
      return await substituteUploadedUrls(this.surveyableIdValue, data)
    } catch (error) {
      logError("response", "Photo URL substitution before save failed", {
        responseId: this.surveyableIdValue,
        message:    error?.message || null
      }).catch(() => {})
      return data
    }
  }

  async submitSurveyData(data) {
    this._ensureSubmissionMeta(data)
    const body = await this._withUploadedPhotoUrls(data)
    // Card #4688 -- a plain save never itself changes the response's lifecycle
    // status (completion is a separate write, see processSurveyData).
    const localStatus = this.responseStatusValue || null
    return fetch(`/api/v1/surveys/results/results.json?surveyable_id=${this.surveyableIdValue}&surveyable_type=${this.surveyableTypeValue}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector("[name='csrf-token']").content,
      },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(timeoutFor("json_write_light", currentLevel()))
    })
      .then(response => {
        // Card #4688 -- one ledger entry per server write, fire-and-forget so
        // a slow/failed ledger write never affects the real save.
        recordLedgerEntry(this.surveyableIdValue, {
          action:            ACTIONS.ANSWERS_SYNC,
          localStatusBefore: localStatus,
          localStatusAfter:  localStatus,
          httpStatus:        response.status,
          serverStatus:      response.headers.get("X-WIN-Response-Status"),
          requestId:         response.headers.get("X-Request-Id")
        }).catch(() => {})

        if (!response.ok) {
          const err = new Error(`Server error: ${response.status}`)
          err.status = response.status
          throw err
        }
        return response.json()
      })
      .then(data => {
        this.survey.fromJSON(data.survey);
        this._setPlaceVariables(data.place_context);
        this.survey.data = data.result;
      })
    // No .catch here -- let callers handle errors
  }

  setupUploadHandlers() {
    // Configure file upload handling for waitForUpload functionality
    this.survey.onUploadFiles.add((survey, options) => {
      console.log("Starting file upload process...", options.files);

      // Show upload progress indicator -- one row per file, updated with
      // size (Card #3492 pt.1) as each compression finishes.
      this.showUploadProgress(options.files);

      // Upload files to server
      this.uploadFilesToServer(options.files, options.name)
        .then(uploadedFiles => {
          console.log("Files uploaded successfully:", uploadedFiles);

          // Call callback with successful uploads - this allows survey completion
          options.callback(uploadedFiles);

          // Hide upload progress
          this.hideUploadProgress();
        })
        .catch(errors => {
          console.error("File upload failed:", errors);

          // Call callback with error messages - this prevents survey completion
          options.callback([], Array.isArray(errors) ? errors : [errors.toString()]);

          // Show error message
          this.showUploadError(errors);
          this.hideUploadProgress();
        });
    });

    this.survey.onClearFiles.add((survey, options) => {
      // The tasker explicitly deleted this photo from the question -- discard
      // the banked vault copy too, or it lingers in My Photos forever. Explicit
      // discard is the one case ADR-0048 permits removing an unconfirmed copy.
      // SurveyJS already applied the value change by the time this fires, so
      // this only does step 2 (discard) and step 3 (server delete) of the
      // single removal contract (lib/photo_removal.js) -- step 1 is already done.
      const value = Array.isArray(options.value) ? options.value : (options.value ? [options.value] : [])
      const cleared = options.fileName
        ? value.filter(f => (f?.name || f) === options.fileName)
        : value
      const urls = cleared
        .map(f => (typeof f === 'string' ? f : f?.content))
        .filter(Boolean)
      discardByUrls(urls).catch(() => {})
      const csrfToken = document.querySelector("[name='csrf-token']")?.content
      for (const url of urls) {
        if (isServerPhotoUrl(url)) deleteServerPhoto(url, csrfToken).catch(() => {})
      }
      options.callback("success")
    });
  }

  // Wire the in-app camera onto the two photo question types. After SurveyJS
  // renders a camera question, hide its default file/camera UI and inject a
  // Take Photo button that dispatches field:photo-camera:open with the question
  // object + multi flag. The singleton field-photo-camera controller catches it.
  //
  // Only engages when the photo-camera sheet is present on the page (field
  // context). Elsewhere the default SurveyJS file UI is left untouched.
  setupInAppCamera() {
    this.survey.onAfterRenderQuestion.add((survey, options) => {
      // Cache every question's rendered element, regardless of camera mode --
      // _markCompressionSkipped (card #3491) needs somewhere to hang a
      // non-blocking marker for ANY photo question, not just the in-app
      // camera ones handled below.
      this._questionElements = this._questionElements || {};
      this._questionElements[options.question.name] = options.htmlElement;

      const mode = options.question.cameraMode; // "single" | "multi" | undefined
      if (mode !== 'single' && mode !== 'multi') return;

      // No sheet on the page (non-field surface) -- leave default UI alone.
      const hasSheet = !!document.querySelector('[data-controller~="field-photo-camera"]');
      if (!hasSheet) return;

      const el = options.htmlElement;
      const question = options.question;
      const multi = mode === 'multi';

      // Hide ONLY SurveyJS's capture/input control (the decorator that would
      // trigger the native camera/file picker). Leave the preview area visible
      // so SurveyJS renders the captured value as normal. The decorator carries
      // the native <input type=file>, which is also our last-resort fallback
      // (revealed by the camera controller on getUserMedia failure), so we hide
      // rather than remove it.
      const decorators = el.querySelectorAll('.sd-file__decorator');
      if (decorators.length) {
        decorators.forEach(node => {
          node.style.display = 'none';
          node.setAttribute('data-photo-camera-hidden', '');
        });
      } else {
        // Fallback for DOM variations -- hide the input's wrapper but keep any
        // sibling preview node untouched.
        el.querySelectorAll('input[type="file"]').forEach(input => {
          const wrap = input.closest('.sd-file__decorator') || input.parentElement;
          if (wrap && !wrap.classList.contains('sd-file__preview')) {
            wrap.style.display = 'none';
            wrap.setAttribute('data-photo-camera-hidden', '');
          }
        });
      }

      // Don't double-inject if SurveyJS re-renders the question.
      if (el.querySelector('[data-photo-camera-target="trigger"]')) {
        this._renderCameraPreview(el, question);
        return;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('data-photo-camera-target', 'trigger');
      button.className = 'w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-sapphire text-white text-sm font-semibold active:opacity-90';
      button.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span>${multi ? 'Take Photos' : 'Take Photo'}</span>
      `;
      button.addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('field:photo-camera:open', {
          detail: { question, multi }
        }));
      });

      el.appendChild(button);

      // Our own thumbnail preview beneath the button -- a guaranteed reflection
      // of question.value (in case SurveyJS's own preview is absent/empty),
      // re-rendered whenever the value changes.
      this._renderCameraPreview(el, question);
      const prevCb = question.valueChangedCallback;
      question.valueChangedCallback = () => {
        if (typeof prevCb === 'function') prevCb();
        this._renderCameraPreview(el, question);
      };
    });
  }

  // Render thumbnails of the question's current value beneath the Take Photo
  // button, mirroring the in-sheet strip look. Reflects the value on every
  // change so the tasker always sees what they captured.
  _renderCameraPreview(el, question) {
    if (this.backgroundUploadValue && this.hasPhotoTileTemplateTarget) {
      this._cameraQuestions.set(question.name, { el, question });
      this._renderBackgroundPreview(el, question);
      this._applyLandedUrls(question);
      return;
    }
    renderPhotoPreview(el, question.value, { onDelete: (url) => this._deletePhoto(question, url) });
  }

  // Single removal entry point for this question (card #4744) -- see
  // lib/photo_removal.js for the full three-step contract (value, discard,
  // server delete). question.value's setter runs SurveyJS's own
  // valueChangedCallback, which is already wired to re-render the preview, so
  // nothing else here needs to repaint anything.
  _deletePhoto(question, url) {
    const csrfToken = document.querySelector("[name='csrf-token']")?.content;
    removePhotoFromQuestion(question, url, { csrfToken }).catch(() => {});
  }

  // ── Background photo upload (card #4097, field:background_photo_upload) ──
  //
  // The upload itself lives in field_image_queue.js (startBackgroundUpload).
  // This side keeps three things true while it runs:
  //   - the preview always shows the photo from the phone (never re-downloaded,
  //     never a broken image), with a status chip under it;
  //   - when a photo lands, the ANSWER is switched to the server URL (the data,
  //     not the pixels), so autosave and every save carry the server copy;
  //   - Complete offers Wait or Finish now while photos are still sending.
  //
  // Per-photo state comes from the queue rows (status, retry time), the
  // uploader's in-memory state (sending now / waiting for a slot), and the XHR
  // progress events.

  _connectBackgroundUpload() {
    this._cameraQuestions = new Map()   // question name -> { el, question }
    this._photoInfo = new Map()         // local or server URL -> { captureId, localUrl, serverUrl, displayUrl, questionName }
    this._photoRows = new Map()         // captureId -> light copy of its queue row
    this._photoProgress = new Map()     // captureId -> upload percentage
    this._hydratedUrls = []             // object URLs made for photos from an earlier page load
    this._rowsLoaded = false

    this._onPhotoLanded = (event) => this._photoLanded(event.detail || {})
    this._onPhotoState = () => this._refreshPhotoChips()
    this._onUploadProgress = (event) => {
      const { blobKey, pct } = event.detail || {}
      if (!blobKey || !this._photoRows.has(blobKey) && !this._knownCapture(blobKey)) return
      this._photoProgress.set(blobKey, pct)
      this._refreshPhotoChips()
    }
    this._onQueueChanged = () => this._schedulePhotoRowRefresh()

    window.addEventListener("field:photo-landed", this._onPhotoLanded)
    window.addEventListener("field:photo-state", this._onPhotoState)
    window.addEventListener("field:upload-progress", this._onUploadProgress)
    window.addEventListener("field:queue-changed", this._onQueueChanged)
    this._schedulePhotoRowRefresh(0)
  }

  _disconnectBackgroundUpload() {
    window.removeEventListener("field:photo-landed", this._onPhotoLanded)
    window.removeEventListener("field:photo-state", this._onPhotoState)
    window.removeEventListener("field:upload-progress", this._onUploadProgress)
    window.removeEventListener("field:queue-changed", this._onQueueChanged)
    if (this._onGateQueueChanged) {
      window.removeEventListener("field:queue-changed", this._onGateQueueChanged)
      window.removeEventListener("field:photo-state", this._onGateQueueChanged)
    }
    clearTimeout(this._photoRowTimer)
    for (const url of this._hydratedUrls || []) {
      try { URL.revokeObjectURL(url) } catch { /* ok */ }
    }
  }

  _knownCapture(captureId) {
    for (const info of this._photoInfo.values()) if (info.captureId === captureId) return true
    return false
  }

  _rememberLocalPhoto(captureId, localUrl, questionName) {
    this._photoInfo.set(localUrl, { captureId, localUrl, serverUrl: null, displayUrl: localUrl, questionName })
  }

  // One preview tile per photo in the answer, cloned from the view's template.
  _renderBackgroundPreview(el, question) {
    let strip = el.querySelector('[data-photo-camera-target="preview"]')
    if (!strip) {
      strip = document.createElement("div")
      strip.setAttribute("data-photo-camera-target", "preview")
      strip.className = "flex gap-2 overflow-x-auto mt-3 pb-1"
      // Card #4395 -- tapping a tile opens the shared viewer, same hand-off
      // field_photo_camera_controller already forwards for #4368. Delegated
      // on the strip (not per-tile) since tiles are replaced wholesale on
      // every render -- see _openBackgroundTile.
      strip.addEventListener("click", (event) => this._openBackgroundTile(event, strip))
      el.appendChild(strip)
    }
    // Owning question, for the delete badge (card #4744) -- _openBackgroundTile
    // routes a delete tap through lib/photo_removal.js, which needs the actual
    // question object to update its value.
    strip._question = question
    const urls = previewUrls(question.value)
    strip.replaceChildren(...urls.map(url => this._buildPhotoTile(url)))
    strip.classList.toggle("hidden", urls.length === 0)
  }

  // Tap handler for the background-upload strip (card #4395, extended #4744
  // for the delete badge each tile now carries). The viewer gets the list of
  // URLs each tile is ACTUALLY showing right now (the phone's copy while
  // saving/offline, the server URL once landed -- i.e. each tile's own
  // rendered <img src>, not the raw answer entry, which can be a dead blob:
  // link after an upload lands). Tiles with nothing to show (not yet
  // hydrated) or a lost cover are skipped entirely, so the tapped tile's
  // position in that filtered list -- not its position in the full strip --
  // is the index handed to the viewer.
  _openBackgroundTile(event, strip) {
    const deleteBtn = event.target.closest("[data-photo-tile-delete]")
    if (deleteBtn) {
      event.stopPropagation()
      const tile = deleteBtn.closest("[data-photo-tile]")
      const url = tile?.dataset.photoUrl
      if (url && window.confirm("Delete this photo?")) this._deletePhoto(strip._question, url)
      return
    }

    const tile = event.target.closest("[data-photo-tile]")
    if (!tile || !strip.contains(tile)) return

    const tiles = Array.from(strip.querySelectorAll("[data-photo-tile]"))
    const viewable = tiles
      .map((t) => ({ tile: t, url: this._tileDisplayUrl(t) }))
      .filter(({ url }) => url)

    const index = viewable.findIndex(({ tile: t }) => t === tile)
    if (index === -1) return

    window.dispatchEvent(new CustomEvent("field:photo-viewer:open", {
      detail: { urls: viewable.map(({ url }) => url), index, onDelete: (url) => this._deletePhoto(strip._question, url) }
    }))
  }

  // The URL a tile is actually displaying -- null when the image is hidden,
  // which _applyPhotoTile already keeps in sync with "nothing to show yet" and
  // "lost" alike, so this reads the DOM rather than re-deriving that state.
  _tileDisplayUrl(tile) {
    const image = tile.querySelector("[data-photo-tile-image]")
    if (!image || image.classList.contains("hidden")) return null
    return image.getAttribute("src") || null
  }

  _buildPhotoTile(url) {
    const tile = this.photoTileTemplateTarget.content.firstElementChild.cloneNode(true)
    tile.dataset.photoUrl = url
    this._applyPhotoTile(tile)
    return tile
  }

  _refreshPhotoChips() {
    this.element.querySelectorAll("[data-photo-tile]").forEach(tile => this._applyPhotoTile(tile))
  }

  // Sets the tile's image and chip from what is known about its photo.
  _applyPhotoTile(tile) {
    const url = tile.dataset.photoUrl
    const info = this._photoInfo.get(url)
    const image = tile.querySelector("[data-photo-tile-image]")
    const lostCover = tile.querySelector("[data-photo-tile-lost]")

    // The phone's copy wins whenever there is one. A blob: link this page
    // cannot resolve is never put in an <img> (it would show broken).
    const displayUrl = info?.displayUrl || (url.startsWith("blob:") ? null : url)
    if (displayUrl && image.getAttribute("src") !== displayUrl) image.setAttribute("src", displayUrl)
    image.classList.toggle("hidden", !displayUrl)

    let state = this._photoState(url, info)
    if (!displayUrl && url.startsWith("blob:") && this._rowsLoaded && state !== "saved") state = "lost"
    lostCover.classList.toggle("hidden", state !== "lost")
    if (state === "lost") image.classList.add("hidden")

    for (const chip of tile.querySelectorAll("[data-photo-tile-chip] [data-state]")) {
      chip.classList.toggle("hidden", chip.dataset.state !== state)
    }
    if (state === "saving") {
      const pct = info ? this._photoProgress.get(info.captureId) : null
      tile.querySelector("[data-chip-text]").textContent = Number.isFinite(pct) ? `Saving ${pct}%` : "Saving"
    }
    if (state === "waiting") {
      const row = info ? this._photoRows.get(info.captureId) : null
      tile.querySelector("[data-chip-detail]").textContent = this._retryLabel(row?.nextRetryAt)
    }
  }

  // saving | queued | saved | waiting | attention | lost | null (nothing to show)
  _photoState(url, info) {
    if (!info) return null
    const live = backgroundUploadState(info.captureId)
    if (live === "active") return "saving"
    if (live === "waiting") return "queued"

    const row = this._photoRows.get(info.captureId)
    if (!row) return info.serverUrl ? "saved" : null
    if (row.status === "uploaded") return "saved"
    if (row.status === "uploading") return "saving"
    if (row.lost) return "lost"
    if (row.status === "failed") return row.failStatus === 0 ? "waiting" : "attention"
    return "waiting"
  }

  _retryLabel(nextRetryAt) {
    if (!nextRetryAt) return ""
    const at = new Date(nextRetryAt)
    if (Number.isNaN(at.getTime()) || at.getTime() <= Date.now()) return ""
    return `Next try ${at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
  }

  _schedulePhotoRowRefresh(delay = 400) {
    clearTimeout(this._photoRowTimer)
    this._photoRowTimer = setTimeout(() => {
      this._refreshPhotoRows().catch(error => {
        logError("upload", "Photo status refresh failed", { responseId: this.surveyableIdValue, message: error?.message || null }).catch(() => {})
      })
    }, delay)
  }

  // Re-reads this response's queue rows. Keeps a light copy (no bytes) for the
  // chips, gives photos from an earlier page load a fresh on-device link, and
  // switches any answer still holding the blob: link of a photo that has
  // since landed (a retry by the sync pass, or a landing this page missed).
  async _refreshPhotoRows() {
    const rows = await db.pending_images.where("surveyableId").equals(String(this.surveyableIdValue)).toArray()
    this._photoRows = new Map()
    for (const row of rows) {
      if (!row.blobKey || row.kind === "shadow") continue
      this._photoRows.set(row.blobKey, {
        status: row.status, failStatus: row.failStatus, nextRetryAt: row.nextRetryAt || null,
        // A lost stored copy is not lost while memory still holds the photo.
        lost: isLost(row) && !heldPhoto(row.blobKey), serverUrl: row.serverUrl || null
      })
      if (row.localUrl && !this._photoInfo.has(row.localUrl)) await this._hydratePhoto(row)
      const info = row.localUrl ? this._photoInfo.get(row.localUrl) : null
      if (info && row.status === "uploaded" && row.serverUrl && !info.serverUrl) {
        info.serverUrl = row.serverUrl
        this._photoInfo.set(row.serverUrl, info)
      }
    }
    this._rowsLoaded = true
    for (const { el, question } of this._cameraQuestions.values()) {
      this._renderBackgroundPreview(el, question)
      this._applyLandedUrls(question)
    }
  }

  // A photo captured before this page loaded: its old blob: link died with
  // that page, so make a new one from memory or the stored copy. Vault rows
  // (card #3995) keep their bytes in pending_blobs; photoPreviewBlob reads
  // those, or a legacy row's embedded blob. The entry is recorded before the
  // read so a second refresh in the meantime does not hydrate it twice.
  async _hydratePhoto(row) {
    const info = { captureId: row.blobKey, localUrl: row.localUrl, serverUrl: row.serverUrl || null, displayUrl: null, questionName: row.questionName }
    this._photoInfo.set(row.localUrl, info)
    if (row.serverUrl) this._photoInfo.set(row.serverUrl, info)

    const bytes = heldPhoto(row.blobKey) || await photoPreviewBlob(row)
    if (!bytes) return
    try {
      info.displayUrl = URL.createObjectURL(bytes)
      this._hydratedUrls.push(info.displayUrl)
    } catch { info.displayUrl = null }
  }

  _photoLanded({ localUrl, serverUrl, surveyableId }) {
    if (String(surveyableId) !== String(this.surveyableIdValue) || !localUrl || !serverUrl) return
    const info = this._photoInfo.get(localUrl)
    if (!info) return
    info.serverUrl = serverUrl
    this._photoInfo.set(serverUrl, info)
    const entry = this._cameraQuestions.get(info.questionName)
    if (entry) this._applyLandedUrls(entry.question)
    this._refreshPhotoChips()

    // Offline tombstone (card #4744): the tasker may have deleted this exact
    // photo from the question WHILE it was still sending -- the delete had
    // nothing to tell the server yet (there was no serverUrl), so it queued
    // a tombstone against the local url instead. Now that the upload has
    // landed, re-key that tombstone to the real serverUrl and flush it.
    const csrfToken = document.querySelector("[name='csrf-token']")?.content
    retargetTombstone(localUrl, serverUrl, csrfToken).catch(() => {})
  }

  // Switches each landed photo's answer entry from its blob: link to the
  // server URL. The tile keeps showing the phone's copy (displayUrl). Deferred
  // a tick so it never runs inside SurveyJS's own value-change callback.
  _applyLandedUrls(question) {
    setTimeout(() => {
      const value = question.value
      if (!Array.isArray(value)) return
      let changed = false
      const next = value.map(entry => {
        const content = typeof entry === "string" ? entry : entry?.content
        const serverUrl = content ? this._photoInfo.get(content)?.serverUrl : null
        if (!serverUrl || serverUrl === content) return entry
        changed = true
        return typeof entry === "string" ? serverUrl : { ...entry, content: serverUrl }
      })
      if (changed) question.value = next
    }, 0)
  }

  // ── Soft completion gate (decision D1) ──

  async _submitAfterPhotoGate(surveyOptions = null) {
    // SurveyJS's "being saved" notice waits until the gate is out of the way.
    this._gateSurveyOptions = surveyOptions
    const sending = await this._photosStillSending()
    if (sending.count === 0) {
      this._startSubmission(surveyOptions)
      return
    }
    this._gateShownAt = Date.now()
    this._gateWaiting = false
    this._renderGate(sending)
    if (this.hasGateTarget) this.gateTarget.classList.remove("hidden")
    this._onGateQueueChanged = () => this._updateGate()
    window.addEventListener("field:queue-changed", this._onGateQueueChanged)
    window.addEventListener("field:photo-state", this._onGateQueueChanged)
    logEvent("upload", "Photo completion gate shown", {
      responseId: this.surveyableIdValue, count: sending.count, sizeBytes: sending.bytes, lost: sending.lost
    }).catch(() => {})
  }

  // Photos of this response that have not reached the server: unsent queue
  // rows, plus any photo the uploader is busy with whose row is not written yet.
  async _photosStillSending() {
    const rows = await db.pending_images.where("surveyableId").equals(String(this.surveyableIdValue)).toArray()
    const unsent = new Map() // capture id -> size
    let lost = 0
    for (const row of rows) {
      if (row.kind !== "queued" || row.submittedAt) continue
      if (isLost(row) && !heldPhoto(row.blobKey)) { lost++; continue }
      if (["pending", "uploading", "reauth-required"].includes(row.status) || (row.status === "failed" && row.failStatus === 0)) {
        unsent.set(row.blobKey || `row-${row.id}`, row.sizeBytes || 0)
      }
    }
    for (const { captureId, sizeBytes } of backgroundBusyIds(this.surveyableIdValue)) {
      if (!unsent.has(captureId)) unsent.set(captureId, sizeBytes || 0)
    }
    let bytes = 0
    for (const size of unsent.values()) bytes += size
    return { count: unsent.size, bytes, lost }
  }

  _renderGate({ count, bytes, lost }) {
    const photos = count === 1 ? "1 photo" : `${count} photos`
    if (this.hasGateTitleTarget) this.gateTitleTarget.textContent = `${photos} still sending (${formatBytes(bytes)})`
    if (this.hasGateBodyTarget) {
      this.gateBodyTarget.textContent = count === 1
        ? "Wait here until it finishes, or finish now and keep the app open until the tick appears."
        : "Wait here until they finish, or finish now and keep the app open until the ticks appear."
    }
    if (this.hasGateProgressTarget) {
      // Uploads in flight right now (at most two); the rest wait their turn.
      const busy = backgroundBusyIds(this.surveyableIdValue).filter(({ captureId }) => backgroundUploadState(captureId) === "active").length
      this.gateProgressTarget.textContent = busy > 0 ? `Sending now: ${busy === 1 ? "1 photo" : `${busy} photos`}` : ""
    }
    if (this.hasGateLostTarget) {
      this.gateLostTarget.textContent = lost === 1
        ? "1 photo couldn't be saved. Retake it in the survey."
        : `${lost} photos couldn't be saved. Retake them in the survey.`
      this.gateLostTarget.classList.toggle("hidden", lost === 0)
    }
  }

  async _updateGate() {
    if (!this.hasGateTarget || this.gateTarget.classList.contains("hidden")) return
    const sending = await this._photosStillSending()
    this._renderGate(sending)
    if (this._gateWaiting && backgroundBusyCount(this.surveyableIdValue) === 0) this._closeGate("wait")
  }

  // Wait: try anything waiting for signal again now, then finish on its own
  // once nothing is sending (each photo has landed or fallen back to the queue).
  async waitForPhotos() {
    if (this._gateWaiting) return
    this._gateWaiting = true
    if (this.hasGateWaitButtonTarget) this.gateWaitButtonTarget.disabled = true
    if (this.hasGateWaitSpinnerTarget) this.gateWaitSpinnerTarget.classList.remove("hidden")
    if (this.hasGateWaitLabelTarget) this.gateWaitLabelTarget.textContent = "Waiting for photos"
    await retryBackgroundNow(this.surveyableIdValue).catch(() => 0)
    await this._updateGate()
  }

  finishWithoutWaiting() {
    this._closeGate("finish_now")
  }

  _closeGate(choice) {
    if (!this.hasGateTarget || this.gateTarget.classList.contains("hidden")) return
    this.gateTarget.classList.add("hidden")
    window.removeEventListener("field:queue-changed", this._onGateQueueChanged)
    window.removeEventListener("field:photo-state", this._onGateQueueChanged)
    logEvent("upload", "Photo completion gate choice", {
      responseId: this.surveyableIdValue, choice, waitedMs: Date.now() - (this._gateShownAt || Date.now())
    }).catch(() => {})
    this._startSubmission(this._gateSurveyOptions)
  }

  // The place's Poor Signal flag: the value Rails rendered on the page, or the
  // device's place cache when the page did not carry it (card #4097 side fix --
  // Declan's phone read false at a flagged place because its cache lacked it).
  async _placeIsPoorSignal() {
    if (this.hasPoorSignalValue) return this.poorSignalValue
    return placeIsPoorSignal(this.placeIdValue)
  }

  // Card #4838: cameraMs (Camera opened to Photo capture returned) and
  // cameraMode ("native" | "in-app") -- null/null when the photo didn't come
  // through the Take Photo flow at all (e.g. a plain file-question upload),
  // since then no "Camera opened" was ever logged for it.
  _logCaptureReturned(startedAt, detail) {
    const now = performance.now()
    const camera = takeCameraOpened()
    logEvent("upload", "Photo capture returned", {
      msToReturn: Math.round(now - startedAt),
      cameraMs: camera ? Math.round(now - camera.openedAt) : null,
      cameraMode: camera ? camera.mode : null,
      ...detail
    }).catch(() => {})
  }

  // Card #3995 -- one photo at a time. This used to start every upload at once
  // (Promise.all): six photos meant six requests in the same second, competing
  // for a weak signal, and all six failed. Photos are now compressed and sent in
  // turn. To keep capture quick on a bad signal, the first upload in a batch
  // that fails for want of a network (a timeout or no connection) tells the
  // rest of the batch to skip their own attempt: they are stored and queued at
  // once, and the sync passes send them later, also one at a time.
  async uploadFilesToServer(files, questionName) {
    const batch = { signalFailed: false }
    const results = []
    for (const [index, file] of files.entries()) {
      results.push(await this.uploadSingleFile(file, questionName, index, batch))
    }

    // Compression failures are skipped, not thrown (see uploadSingleFile) --
    // partition them out here rather than letting one bad photo collapse
    // the whole batch. Whatever succeeded still completes the question.
    const succeeded = results.filter(r => !r.compressionFailed)
    const failed    = results.filter(r => r.compressionFailed)
    if (failed.length > 0) this._markCompressionSkipped(questionName, failed.map(r => r.reason))

    return succeeded.map(r => ({ file: r.originalFile, content: r.url }))
  }

  async uploadSingleFile(file, questionName, index = 0, batch = { signalFailed: false }) {
    // Card #4097 -- "Photo capture returned" measures how long the Tasker is
    // held up per photo, before and after background upload.
    const captureStartedAt = performance.now()
    logEvent("upload", "Photo captured", {
      originalSize: file.size,
      type: file.type,
      question: questionName
    }).catch(() => {})

    // Step 1: Compress to WebP (or JPEG fallback) before upload, retrying with
    // smaller/lower-quality presets if the first attempt fails or the source
    // is too large for the canvas pipeline (low-memory Android). HEIC that the
    // browser can't read is converted to JPEG first (card #3363). Exhaustion used to throw CompressionError, which propagated up
    // through uploadFilesToServer's Promise.all and failed the ENTIRE batch --
    // discarding every other, perfectly good photo in the same upload -- then
    // surfaced a blocking showUploadError() toast that stopped the tasker
    // mid-mission (card #2433's original "no silent fallback" choice).
    // Reversed per Jay on card #3491: this one photo is skipped quietly and
    // the response can still complete. uploadFilesToServer partitions this
    // sentinel out of the batch, and _markCompressionSkipped gives the tasker
    // a non-blocking marker instead. The compression failure is still logged
    // exactly as before.
    let compressed
    try {
      compressed = await compressImageWithRetry(file)
    } catch (error) {
      logError("upload", "Photo compression exhausted", {
        message: error?.message,
        cause: error?.cause?.message || error?.cause?.name || null,
        size: file.size,
        reason: error?.reason || null,
        detectedType: error?.detectedType || file.type || null,
        heicConversionAttempted: !!error?.heicConversionAttempted
      }).catch(() => {})
      return { compressionFailed: true, originalFile: file, reason: error?.reason || COMPRESSION_FAILURE.DECODE_FAILED }
    }

    // Card #3492 pt.1 -- show the resulting compressed size the moment it's
    // known, so the tasker can see what's actually about to go over the wire
    // (reflects the raised 30MB cap from #3494) rather than a bare spinner.
    this._setUploadRowStatus(index, formatBytes(compressed.size))

    // Step 2: Rename to structured filename for traceability (lib/image_filename.js,
    // shared with the offline shell, card #3827)
    const named = withImageFilename(compressed, {
      responseId:   this.surveyableIdValue,
      questionName: questionName,
      userId:       this.userIdValue
    })

    // Card #4097 -- field:background_photo_upload. The upload starts from memory
    // (the #3994 rule below still holds), a queue row is written beside it, and
    // the survey gets the on-device photo straight back. Nobody waits on it.
    if (this.backgroundUploadValue) {
      const backgroundPoorSignal = await this._placeIsPoorSignal()
      const { captureId: backgroundId, localUrl } = startBackgroundUpload(named, {
        surveyableId:   this.surveyableIdValue,
        surveyableType: this.surveyableTypeValue,
        responseId:     this.surveyableIdValue,
        placeId:        this.placeIdValue || null,
        questionName:   questionName || null,
        poorSignal:     backgroundPoorSignal
      })
      this._rememberLocalPhoto(backgroundId, localUrl, questionName)
      this._setUploadRowStatus(index, `${formatBytes(compressed.size)} · saving`)
      this._logCaptureReturned(captureStartedAt, { mode: "background", sizeBytes: compressed.size, poorSignal: backgroundPoorSignal })
      return { originalFile: file, url: localUrl, filename: named.name, background: true }
    }

    // Card #3994 -- ALWAYS try the network first, from the bytes still in memory,
    // whatever the connectivity: normal, Degraded, a Poor Signal place, offline,
    // online_only. Queuing a photo straight away put it into IndexedDB, and an
    // iPhone often cannot read it back (UnknownError / NotFoundError, sometimes
    // within seconds). The photo is queued only when this attempt fails or times
    // out, and the same bytes stay in memory for the retries
    // (field_image_queue.js holdPhotoInMemory). Poor Signal and Degraded get the
    // longer timeout; the place's flag is read from the device's place cache.
    //
    // Transport stays plain fetch (via observedFetch, so the attempt still feeds
    // the connectivity evidence), not the XHR-with-progress helper the retry path
    // uses -- this capture-time call is covered by several existing regression
    // specs that intercept window.fetch.
    this._setUploadRowStatus(index, `${formatBytes(compressed.size)} · uploading`)
    const captureId  = newCaptureId()
    const poorSignal = await this._placeIsPoorSignal()
    // An earlier photo in this batch found no network: do not wait out another
    // timeout for this one, queue it now (its stored copy is verified there).
    const attempt = batch.signalFailed
      ? { ok: false, kind: "skipped" }
      : await uploadPhotoFromMemory(named, {
          captureId,
          surveyableId:   this.surveyableIdValue,
          surveyableType: this.surveyableTypeValue,
          poorSignal
        })
    if (!attempt.ok && (attempt.kind === "timeout" || attempt.kind === "network")) batch.signalFailed = true

    if (!attempt.ok) {
      await this._logUploadFallback(attempt, questionName)
      const { localUrl } = await queueImage(named, {
        surveyableId:   this.surveyableIdValue,
        surveyableType: this.surveyableTypeValue,
        responseId:     this.surveyableIdValue,
        placeId:        this.placeIdValue || null,
        questionName:   questionName || null,
        blobKey:        captureId
      })
      this._setUploadRowStatus(index, `${formatBytes(compressed.size)} · saved offline`)
      this._logCaptureReturned(captureStartedAt, { mode: "awaited", sizeBytes: compressed.size, poorSignal, outcome: "queued" })
      // Return local blob: URL -- survey displays the image immediately.
      // Sync controller will replace with server URL later.
      return { originalFile: file, url: localUrl, filename: named.name, queued: true }
    }

    const data = attempt.data
    this._setUploadRowStatus(index, `${formatBytes(compressed.size)} · uploaded`)

    // Bank a SHADOW recovery copy of this online capture (W1, ADR-0048). The
    // survey itself stores the SERVER url (data.url) exactly as before -- no
    // behavioural change. The shadow copy in pending_images is the safety net,
    // purged only once the whole response is confirmed synced online
    // (purgeForResponse, called from _submitWithOfflineFallback).
    try {
      await bankShadowImage(named, {
        surveyableId:   this.surveyableIdValue,
        surveyableType: this.surveyableTypeValue,
        serverUrl:      data.url,
        responseId:     this.surveyableIdValue,
        placeId:        this.placeIdValue || null,
        questionName:   questionName || null
      })
    } catch { /* shadow backup is best-effort -- never block the online capture */ }

    this._logCaptureReturned(captureStartedAt, { mode: "awaited", sizeBytes: compressed.size, poorSignal, outcome: "uploaded" })
    return { originalFile: file, url: data.url, filename: data.filename }
  }

  // Why the capture-time upload did not land, in the same log lines as before
  // card #3994 (a failed request, a non-2xx, a 2xx that carried no photo).
  //
  // Card #4542 -- forced-offline mode now makes the service worker reject the
  // fetch outright (Response.error()) rather than resolve a synthetic 503, so
  // attempt.kind is "network" for it, same as a real dropped connection, and
  // it falls through to the generic branch below (an expected queue, same
  // treatment as any other network failure). The 503 + { offline: true } body
  // check below is kept only so a device still running an OLDER cached
  // service worker (pre-#4542, mid-rollout) still gets the quiet treatment
  // it always did -- harmless and removable once every device has picked up
  // the new SW.
  async _logUploadFallback(attempt, questionName) {
    const context = { responseId: this.surveyableIdValue, questionName: questionName || null }

    if (attempt.kind === "http") {
      if (attempt.status === 503) {
        const body = await attempt.response.json().catch(() => ({}))
        if (body.offline) return
      }
      logError("upload", "Photo upload failed, queued for retry", {
        status: attempt.status, statusText: attempt.statusText, ...context
      }).catch(() => {})
    } else if (attempt.kind === "skipped") {
      // Card #3995 -- an earlier photo in the same batch had no network.
      logInfo("upload", "Photo upload skipped, no signal earlier in this batch", context).catch(() => {})
    } else if (attempt.kind === "not_attached") {
      // Card #3990 -- 2xx for a photo the server could not attach or did not
      // receive; there is no server url to put in the answer.
      logError("upload", "Photo upload accepted but not attached, queued", {
        status: attempt.data?.status || null, reason: attempt.data?.reason || null, ...context
      }).catch(() => {})
    } else {
      logError("upload", "Photo upload request failed, queued for retry", {
        message: attempt.message || null, ...context
      }).catch(() => {})
    }
  }

  // One row per file (Card #3492), each updated in place via
  // _setUploadRowStatus as compression resolves and the upload lands --
  // replaces the old single opaque "Uploading N images..." spinner with a
  // per-photo size + outcome the tasker can actually read.
  showUploadProgress(files) {
    let progressDiv = document.getElementById('survey-upload-progress');
    if (!progressDiv) {
      progressDiv = document.createElement('div');
      progressDiv.id = 'survey-upload-progress';
      progressDiv.className = 'fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-white border border-slate-200 text-slate-700 px-4 py-3 rounded-xl shadow-lg w-72 max-w-[90vw] space-y-2';
      document.body.appendChild(progressDiv);
    }

    progressDiv.innerHTML = files.map((file, index) => `
      <div class="flex items-center gap-2.5" data-upload-row="${index}">
        <svg class="animate-spin h-4 w-4 text-sapphire shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <div class="flex-1 min-w-0">
          <p class="text-xs font-medium text-slate-700 truncate">${this.backgroundUploadValue ? 'Saving' : 'Uploading'} photo${files.length > 1 ? ` ${index + 1} of ${files.length}` : ''}</p>
          <p class="text-[11px] text-slate-400" data-upload-status>Compressing&hellip;</p>
        </div>
      </div>
    `).join('');
  }

  // Updates one row's status line (size, and later its outcome) in place.
  // No-op if the toast/row is already gone (e.g. hideUploadProgress ran, or
  // this callback lands after the batch finished) -- purely cosmetic, never
  // worth throwing over.
  _setUploadRowStatus(index, text) {
    const status = document.getElementById('survey-upload-progress')
      ?.querySelector(`[data-upload-row="${index}"] [data-upload-status]`)
    if (status) status.textContent = text
  }

  hideUploadProgress() {
    const progressDiv = document.getElementById('survey-upload-progress');
    if (progressDiv) {
      progressDiv.remove();
    }
  }

  // Non-blocking visibility for a skipped (compression-failed) photo, card
  // #3491. Deliberately NOT another showUploadError() toast -- the card is
  // explicit that a floating toast is what stopped the tasker mid-mission in
  // the first place. Instead this hangs a small marker directly on the
  // question's own rendered element (cached by every onAfterRenderQuestion
  // call in setupInAppCamera), mirroring field_photos_controller.js's
  // per-row "too_large" status treatment adapted to a per-question spot since
  // surveys don't have an upload-row list. Amber ("needs attention"), not
  // red ("Error/Failed") -- color-system.md reserves red for a genuine
  // blocking failure, and this one explicitly isn't. Self-clears after 15s --
  // purely informational, never requires a dismiss click, and never blocks
  // navigating away from or completing the question.
  //
  // Card #3363: `reasons` is one COMPRESSION_FAILURE value per skipped file,
  // so a HEIC that couldn't be converted or a Live Photo's .mov says what
  // actually went wrong instead of "too large". One sentence per reason.
  _markCompressionSkipped(questionName, reasons) {
    const el = this._questionElements?.[questionName]
    if (!el) return

    let marker = el.querySelector('[data-compression-skip-marker]')
    if (!marker) {
      marker = document.createElement('div')
      marker.setAttribute('data-compression-skip-marker', '')
      marker.className = 'mt-2 inline-block rounded-lg bg-amber-100 px-3 py-1.5 text-xs font-medium text-amber-800'
      el.appendChild(marker)
    }

    const counts = reasons.reduce((tally, reason) => {
      tally[reason] = (tally[reason] || 0) + 1
      return tally
    }, {})
    marker.textContent = Object.entries(counts)
      .map(([reason, count]) => this._compressionSkipMessage(reason, count))
      .join(' ')

    clearTimeout(marker._compressionSkipTimer)
    marker._compressionSkipTimer = setTimeout(() => marker.remove(), 15000)
  }

  _compressionSkipMessage(reason, count) {
    const one = count === 1
    switch (reason) {
      case COMPRESSION_FAILURE.HEIC_CONVERSION_FAILED:
        return one
          ? "This HEIC photo couldn't be converted. Save it as JPEG and try again."
          : `${count} HEIC photos couldn't be converted. Save them as JPEG and try again.`
      case COMPRESSION_FAILURE.VIDEO:
        return one
          ? "Videos can't be added. Use the photo instead."
          : `${count} videos can't be added. Use the photos instead.`
      default:
        return one
          ? 'Photo too large, skipped. You can carry on.'
          : `${count} photos too large, skipped. You can carry on.`
    }
  }

  showUploadError(errors) {
    // Create error notification
    const errorDiv = document.createElement('div');
    errorDiv.className = 'fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg max-w-md';

    const errorMessage = Array.isArray(errors) ? errors.join(', ') : errors.toString();
    errorDiv.innerHTML = `
      <div class="flex items-center justify-between">
        <div>
          <strong>Upload Failed:</strong> ${errorMessage}
        </div>
        <button onclick="this.parentElement.parentElement.remove()" class="ml-4 text-white hover:text-gray-200">
          <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
          </svg>
        </button>
      </div>
    `;

    document.body.appendChild(errorDiv);

    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (errorDiv.parentElement) {
        errorDiv.remove();
      }
    }, 10000);
  }

  // Card #4451 -- relaxRequired must only be true for an MC re-editing a
  // response that has already ended (the Sept 2025 intent, commit ad1206c19).
  // A first-time Tasker completion (todo/started) needs isRequired left
  // alone, online and offline, or the survey silently allows blank required
  // questions to submit.
  convertPhotoTypeQuestionsToFileUpload(data, relaxRequired) {
    // Convert photo/camera-related questions in the survey JSON to file upload type
    if (typeof data !== 'object' || data === null || !Array.isArray(data.pages)) return;

    const convertElements = (elements) => {
      if (!Array.isArray(elements)) return;
      elements.forEach(element => {
        const type = element.type;
        if (
          type === 'file' ||
          type === 'camera-upload-question' ||
          type === 'camera-mult-upload-question'
        ) {
          // Preserve the ORIGINAL camera type before we flatten to "file" so the
          // in-app photo camera can decide single vs multi capture.
          if (type === 'camera-upload-question') element.cameraMode = 'single';
          if (type === 'camera-mult-upload-question') element.cameraMode = 'multi';

          element.type = 'file';
          // Single-photo question is exactly one file; everything else keeps the
          // prior multi-file behaviour.
          element.allowMultiple = type !== 'camera-upload-question';
          element.sourceType = 'file-camera';
          element.waitForUpload = true;  // Enable waitForUpload for file questions
          element.storeDataAsText = false;  // Store file URLs, not base64
          // Match the compressor's input cap (#3494, #3733) -- SurveyJS rejects
          // anything over maxSize before our compression pipeline ever runs.
          element.maxSize = MAX_INPUT_BYTES;
        }

        // Only relax required-question enforcement for an already-ended
        // response being re-edited (MC flow). First-time completions keep
        // whatever isRequired the survey definition set.
        if (relaxRequired) element.isRequired = false;

        // If the element is a panel, recursively convert its inner elements
        if (type === 'panel' && Array.isArray(element.elements)) {
          convertElements(element.elements);
        }
      });
    };

    data.pages.forEach(page => {
      convertElements(page.elements);
    });
  }

  addCancelButton() {
    // Add a cancel button to the survey navigation
    this.survey.addNavigationItem({
      id: "survey_cancel",
      title: "Cancel",
      tooltip: "Cancel editing and return without saving changes",
      action: () => {
        if (confirm("Are you sure you want to cancel editing? Any changes will be lost.")) {
          // Show the lock div again
          const lockDiv = document.getElementById(`response_${this.surveyableIdValue}_unlock_icon`);
          if (lockDiv) {
            lockDiv.classList.remove('hidden');
          }

          // Navigate back or reload the page to exit edit mode
          // You can customize this behavior as needed
          window.location.reload();
        }
      }
    });
  }
}
